// Backend'deki Plan şeması ile frontend'in beklediği alan adları farklı.
//
// Veritabanı (Prisma PlanFeature/PlanLimit) camelCase ve 14 özellik + 2 limit
// içeriyor. Frontend ise snake_case ve 30'dan fazla anahtar okuyor. Bu yüzden
// plan nesnesi doğrudan kullanılamıyor: `features.get_request` gibi okumalar
// undefined dönüyor ve tüm özellikler kilitli görünüyordu.
//
// Burası tek dönüşüm noktası. Plan okuyan her yer normalizePlan()'dan geçmeli.

// Aboneliği olmayan kullanıcı için varsayılan plan.
export const FREE_PLAN = {
  id: null,
  name: 'Ücretsiz',
  price: 0,
  durationDays: 30,
  isActive: true,
  isSpecial: false,
  features: {
    http_monitoring: true,
    ping_monitoring: true,
    port_monitoring: true,
    keyword_monitoring: true,
    cronjob_monitoring: false,
    dns_monitoring: false,
    ssl_monitoring: false,
    maintenance_mode: false,
    status_code_alerts: false,
    slow_response_alerts: false,
    request_timeout_alerts: true,
    email_notification: true,
    sms_notification: false,
    api_access: false,
    team_access: false,
    uptime_reports: false,
    multi_region: false,
    region_monitoring: false,
    advance_request: false,
    get_request: false,
    post_request: false,
    put_request: false,
    patch_request: false,
    delete_request: false,
    options_request: false,
    head_request: true,
    second_interval: false,
    minute_interval: true,
    hour_interval: true,
  },
  limits: {
    monitor_count: 20,
    max_monitors: 20,
    min_interval: 5,
    max_interval: 60,
    team_members: 0,
    max_team_members: 0,
  },
}

// Şemada karşılığı OLMAYAN, dolayısıyla veriyle kısıtlanamayan yetenekler.
// Bunları kilitlemek keyfi olurdu; ücretli planda açık kabul ediyoruz.
// Gerçekten paket bazlı kısıtlanmaları isteniyorsa PlanFeature'a alan eklenmeli.
const UNMODELLED_ENABLED = {
  http_monitoring: true,
  ping_monitoring: true,
  port_monitoring: true,
  keyword_monitoring: true,
  request_timeout_alerts: true,
  email_notification: true,
  advance_request: true,
  minute_interval: true,
  hour_interval: true,
}

// Şemada karşılığı olmayan VE backend'de henüz hiç kodu bulunmayan yetenekler.
// (DNS monitör tipi yok; Region/Team modelleri şemada var ama kodsuz.)
const UNBUILT_DISABLED = {
  dns_monitoring: false,
  multi_region: false,
  region_monitoring: false,
  team_access: false,
  api_access: false,
  sms_notification: false,
  uptime_reports: false,
}

/**
 * Prisma'dan gelen ham plan nesnesini frontend'in beklediği şekle çevirir.
 * Plan yoksa ya da features/limits ilişkileri gelmemişse FREE_PLAN döner.
 */
export const normalizePlan = (rawPlan) => {
  const f = rawPlan?.features
  const l = rawPlan?.limits
  if (!f || !l) return FREE_PLAN

  return {
    id: rawPlan.id,
    name: rawPlan.name,
    price: rawPlan.price,
    durationDays: rawPlan.durationDays,
    isActive: rawPlan.isActive,
    isSpecial: rawPlan.isSpecial,
    features: {
      ...UNMODELLED_ENABLED,
      ...UNBUILT_DISABLED,
      // Şemada birebir karşılığı olanlar
      get_request: !!f.getRequest,
      post_request: !!f.postRequest,
      put_request: !!f.putRequest,
      patch_request: !!f.patchRequest,
      delete_request: !!f.deleteRequest,
      options_request: !!f.optionsRequest,
      head_request: !!f.headRequest,
      ssl_monitoring: !!f.sslMonitoring,
      ssl_expire_monitoring: !!f.sslExpireMonitoring,
      domain_expire_monitoring: !!f.domainExpireMonitoring,
      cronjob_monitoring: !!f.cronJobMonitoring,
      maintenance_mode: !!f.maintenanceMode,
      status_code_alerts: !!f.statusCodeMonitoring,
      slow_response_alerts: !!f.slowResponseAlert,
      // Saniyelik kontrol ancak alt sınır 1 dakikanın altındaysa anlamlı
      second_interval: Number(l.minIntervalMinutes) < 1,
    },
    limits: {
      // Frontend iki ismi de kullanıyor, ikisini de dolduruyoruz
      monitor_count: l.maxMonitors,
      max_monitors: l.maxMonitors,
      min_interval: l.minIntervalMinutes,
      // Şemada üst sınır yok; kısıtlamamak için geniş bir değer
      max_interval: 1440,
      // Team özelliği henüz kurulmadı
      team_members: 0,
      max_team_members: 0,
    },
  }
}

/** Redux'taki kullanıcıdan aktif planı çıkarır ve normalize eder. */
export const getUserPlan = (user) => {
  const subscription =
    user?.subscriptions?.find((s) => s.status === 'ACTIVE') ??
    user?.subscriptions?.[0]
  return normalizePlan(subscription?.plan)
}
